import { defineMessages } from 'react-intl';

export const orderConfirmationMessages = defineMessages({
    printReceipt: {
        id: 'confirmation.printReceipt',
        description: 'Label for printing the receipt.',
        defaultMessage: 'Print Receipt'
    },
    orderHeading: {
        id: 'confirmation.orderHeading',
        description: 'The heading on the order confirmation page',
        defaultMessage: "We've received your order."
    },
    purchaserThanks: {
        id: 'confirmation.purchaserThanks',
        description: 'Sentences beneath the order heading thanking the purchaser',
        defaultMessage: `
            Thanks! We received your order and it is being reviewed. We'll send an email confirmation to
            {purchaserEmail} once the order is approved and your payment is processed.
        `
    },
    orderNumber: {
        id: 'confirmation.orderNumber',
        description: 'Label and value for the order number in confirmation',
        defaultMessage: 'Your Order Number: {orderNumber}'
    },
    cardInformation: {
        id: 'confirmation.cardInformation',
        description: 'Table header for the column containing card information such as the faceplate image and value',
        defaultMessage: 'Card Information'
    },
    quantityHeader: {
        id: 'confirmation.quantityHeader',
        description: 'Table header for the column containing the quantity of this card in the order',
        defaultMessage: 'Quantity'
    },
    priceHeader: {
        id: 'confirmation.priceHeader',
        description: 'Table header for the column containing the purchase price of each card in this line item',
        defaultMessage: 'Price'
    },
    totalHeader: {
        id: 'confirmation.totalHeader',
        description: 'Table header for the column containing the total purchase price of the card(s) in this line item',
        defaultMessage: 'Total'
    },
    cardValue: {
        id: 'confirmation.cardValue',
        description: 'Label and value for the card value/denomination in the order confirmation table',
        defaultMessage: 'Card Value: {value}'
    },
    recipientEmail: {
        id: 'confirmation.recipientEmail',
        description: 'Label and value for the recipient email in the order confirmation table',
        defaultMessage: 'Sent to: {email}'
    },
    totalLabel: {
        id: 'confirmation.orderTotalLabel',
        description: 'Label for the total cost of the order',
        defaultMessage: 'Total'
    },
    paymentMethod: {
        id: 'confirmation.paymentMethod',
        description: 'Label for the paymentMethod in confirmation',
        defaultMessage: 'Payment method: {card} ending in {number} '
    },
    paymentStatement: {
        id: 'confirmation.paymentStatement',
        description: "Label for the statement to appear on the purchaser's credit card bill",
        defaultMessage: 'This will appear on your bill as {statement}'
    },
    itemDetailsHeading: {
        id: 'confirmation.itemDetailsHeading',
        description: 'Heading for list of confirmation items',
        defaultMessage: "Order Details"
    },
    itemDetailsEgiftLabel: {
        id: 'confirmation.itemDetailsEgiftLabel',
        description: 'Label for eGift section of confirmation items',
        defaultMessage: "eGift Cards ({eGiftQuantity})"
    },
    itemDetailsPhysicalLabel: {
        id: 'confirmation.itemDetailsPhysicalLabel',
        description: 'Label for Physical card section of confirmation items',
        defaultMessage: "Physical Gift Cards ({physicalQuantity})"
    },
    continueShopping: {
        id: 'confirmation.continueShopping',
        description: 'Label for button on next steps that links to home screen',
        defaultMessage: "Shop for More Cards"
    },
    nextSteps: {
        id: 'confirmation.nextSteps',
        description: 'Heading for next step panel in confirmation page',
        defaultMessage: "Next Steps"
    },
    nextStepsConfirmationEmail: {
        id: 'confirmation.nextStepsConfirmationEmail',
        description: 'Next steps panel notify to check email for confirmation.',
        defaultMessage: 'Check your email for a confirmation which we sent to {email}'
    }
});

export const orderConfirmationSummaryMessages = defineMessages({
    heading: {
        id: 'confirmation.orderSummaryHeading',
        description: 'The heading on the order confirmation summary',
        defaultMessage: "Thank You For Your Order"
    },
    description: {
        id: 'confirmation.description',
        description: 'A brief description below the heading.',
        defaultMessage: "We received your order and are working on it now. Here’s some helpful information for your records."
    },
    orderNumberLabel: {
        id: 'confirmation.orderNumberLabel',
        description: 'Label for the order number in confirmation',
        defaultMessage: 'Order Number:'
    },
    ccLastFour: {
        id: 'confirmation.ccLastFour',
        description: 'Label for the card info in the confirmation summary.',
        defaultMessage: 'Payment made with card ending in {ccLastFour}'
    },
    ccStatementName: {
        id: 'confirmation.ccStatementName',
        description: 'Label for the credit card statement name.',
        defaultMessage: 'The charge on your statement will appear as {ccStatementName}'
    }
});

export const itemConfirmationMessages = defineMessages({
    quantity: {
        id: 'confirmation.quantityLabel',
        description: 'Label for the item quantity display',
        defaultMessage: "{quantity} cards"
    },
    amount: {
        id: 'confirmation.amountLabel',
        description: 'Label for the item amount display',
        defaultMessage: "{amount} each"
    },
    discount: {
        id: 'confirmation.discountLabel',
        description: 'Label for line item discounts on the confirmation summary',
        defaultMessage: '{discount} Discount on {quantity} cards',
    },
    shippingCost: {
        id: 'confirmation.shippingCostLabel',
        description: 'Label for the item shipping amount display',
        defaultMessage: "Shipping {amount}"
    },
    selfBuyRecipientLabel: {
        id: 'confirmation.selfBuyRecipientLabel',
        description: 'Label for selfBuy recipientFirstName value',
        defaultMessage: 'Your'
    },
    
    productPurchaseFee: {
        id: 'confirmation.productPurchaseFeeLabel',
        description: 'Label for the item purchase fee display',
        defaultMessage: "Fee/card {fee}"
    },
});

export const itemConfirmationSummaryMessages = defineMessages({
    heading: {
        id: 'confirmation.itemSummaryHeading',
        description: 'The heading on the item confirmation summary',
        defaultMessage: "Order Summary"
    },
    subtotalLabel: {
        id: 'confirmation.subtotalLabel',
        description: 'Label for subtotal: sum of total face value of cards',
        defaultMessage: "Card Subtotal ({quantity})"
    },
    shippingLabel: {
        id: 'confirmation.shippingLabel',
        description: 'Label for the total shipping cost of order',
        defaultMessage: 'Shipping'
    },
    feesLabel: {
        id: 'confirmation.feesLabel',
        description: 'Label for total fees on order',
        defaultMessage: 'Fees'
    },
    discountsLabel: {
        id: 'confirmation.discountsLabel',
        description: 'Label for total of discounts on order',
        defaultMessage: 'Discounts'
    },
    totalLabel: {
        id: 'confirmation.itemTotalLabel',
        description: 'Label for total amount paid for order',
        defaultMessage: 'Total'
    }
});

export const deliveryDetailsMessages = defineMessages({
    emailLabel: {
        id: 'confirmation.emailLabel',
        description: 'Label for email delivery info',
        defaultMessage: 'Emailed to:'
    },
    deliveryEmail: {
        id: 'confirmation.deliveryEmail',
        description: 'Details for delivery of digital items',
        defaultMessage: '{isSelfBuy, select, true {{confirmationEmail}} other {{recipientEmail}} }'
    },
    deliveryDateLabel: {
        id: 'confirmation.deliveryTimeLabel',
        description: 'Label for requested delivery date.',
        defaultMessage: 'Date:'
    },
    deliveryToday: {
        id: 'confirmation.deliveryToday',
        description: 'Used in place of date string if delivery is today.',
        defaultMessage: 'Today'
    },
    shippingMethodRequiredValidation: {
        id: 'confirmation.shippingMethodRequired',
        description: 'Shipping method required for physical line items',
        defaultMessage: 'Shipping Method is a required field'
    },
    shipToLabel: {
        id: 'confirmation.shipToLabel',
        description: 'Ship to label',
        defaultMessage: 'Ship to:'
    }
});

export const OrderPointsMessages = defineMessages({
    FlexRewardsDollarsUsed: {
        id: 'orderSummary.FlexRewardsDollarsUsed',
        description: 'Flex Rewards Dollars Used text',
        defaultMessage: 'Flex Rewards Dollars Used',
    },
    FlexRewardsDollarsRemaining: {
        id: 'orderSummary.FlexRewardsDollarsRemaining',
        description: 'Flex Rewards Dollars Remaining text',
        defaultMessage: 'Flex Rewards Dollars Remaining',
    },
});
